import "./quiz.scss";

const selectors = {
  quiz: "[data-quiz]",
  quizQuestion: "[data-quiz-question]",
  quizProgressLabel: "[data-quiz-progress-label]",
  quizProgressIndicator: "[data-quiz-progress-indicator]",
  quizResult: "[data-quiz-result]",
  quizReset: "[data-quiz-reset]",
  quizSubscription: "[data-quiz-subscription]",

  calculator: "[data-calculator]",
  calculatorReset: "[data-calculator-reset]",
  calculatorProgressLabel: "[data-calculator-progress-label]",
  calculatorProgressIndicator: "[data-calculator-progress-indicator]",
  calculatorForm: "[data-calculator-form]",
  calculatorResult: "[data-calculator-result]",
  calculatorTrees: "[data-calculator-trees]",
  calculatorKilometers: "[data-calculator-kilometers]",
  calculatorShowers: "[data-calculator-showers]",
  calculatorPapers: "[data-calculator-papers]",
  calculatorMeals: "[data-calculator-meals]",

  active: "active",
};

class Quiz {
  steps;
  activeStep;
  questions;
  subscriptions;
  result;
  reset;
  progressLabel;
  progressIndicator;
  answers;

  constructor(quiz) {
    this.questions = quiz.querySelectorAll(selectors.quizQuestion);
    this.subscriptions = quiz.querySelectorAll(selectors.quizSubscription);
    this.result = quiz.querySelector(selectors.quizResult);
    this.reset = quiz.querySelector(selectors.quizReset);
    this.progressLabel = quiz.querySelector(selectors.quizProgressLabel);
    this.progressIndicator = quiz.querySelector(
      selectors.quizProgressIndicator
    );

    this.answers = {};
    this.activeStep = 1;
    this.steps = [...this.questions, this.result];

    this.setStep(this.activeStep);

    this.questions.forEach((question) =>
      question.addEventListener("submit", (e) => this.handleQuestionSubmit(e))
    );
    this.reset.addEventListener("click", () => this.resetQuiz());
  }

  setStep(activeStep) {
    this.activeStep = activeStep;

    this.setProgressLabel(activeStep);
    this.setProgressIndicator(activeStep);
    this.setStepVisibility(activeStep);

    if (activeStep === this.steps.length) {
      this.getSubscription();
    }
  }

  resetQuiz() {
    this.activeStep = 1;

    this.setProgressLabel(1);
    this.setProgressIndicator(1);
    this.setStepVisibility(1);
  }

  handleQuestionSubmit(event) {
    event.preventDefault();

    const key = event.target.dataset.quizQuestion;
    const formData = new FormData(event.target);

    this.answers = { ...this.answers, [key]: formData.get(key) };

    this.setStep(this.activeStep + 1);
  }

  getSubscription() {
    const activeSubscriptionIndex = [...this.subscriptions].findIndex(
      (subscription) => {
        const subscriptionValues =
          subscription.dataset.quizSubscription.split(";");

        return Object.values(this.answers).every(
          (value, index) => +value <= +subscriptionValues[index]
        );
      }
    );

    this.subscriptions.forEach((subscription, index) =>
      subscription.classList[
        activeSubscriptionIndex === index ? "add" : "remove"
      ](selectors.active)
    );
  }

  setStepVisibility(activeStep) {
    this.steps.forEach((step, index) =>
      step.classList[activeStep - 1 === index ? "add" : "remove"](
        selectors.active
      )
    );
  }

  setProgressLabel(activeStep) {
    this.progressLabel.innerText = this.progressLabel.dataset.quizProgressLabel
      .replace("#", activeStep)
      .replace("#", this.steps.length);
  }

  setProgressIndicator(activeStep) {
    this.progressIndicator.style.setProperty(
      "--quiz-progress",
      (activeStep / this.steps.length) * 100 + "%"
    );
  }
}

class Calculator {
  steps;
  activeStep;
  form;
  result;
  reset;
  progressLabel;
  progressIndicator;
  treesValue;
  kilometersValue;
  showersValue;
  papersValue;
  mealsValue;

  constructor(calculator) {
    this.form = calculator.querySelector(selectors.calculatorForm);
    this.result = calculator.querySelector(selectors.calculatorResult);
    this.reset = calculator.querySelector(selectors.calculatorReset);
    this.progressLabel = calculator.querySelector(
      selectors.calculatorProgressLabel
    );
    this.progressIndicator = calculator.querySelector(
      selectors.calculatorProgressIndicator
    );
    this.treesValue = calculator.querySelector(selectors.calculatorTrees);
    this.kilometersValue = calculator.querySelector(
      selectors.calculatorKilometers
    );
    this.showersValue = calculator.querySelector(selectors.calculatorShowers);
    this.papersValue = calculator.querySelector(selectors.calculatorPapers);
    this.mealsValue = calculator.querySelector(selectors.calculatorMeals);

    this.activeStep = 1;
    this.steps = [this.form, this.result];

    this.setStep(this.activeStep);

    this.form.addEventListener("submit", (e) => this.handleFormSubmit(e));
    this.reset.addEventListener("click", () => this.resetCalculator());
  }

  setStep(activeStep) {
    this.activeStep = activeStep;

    this.setProgressLabel(activeStep);
    this.setProgressIndicator(activeStep);
    this.setStepVisibility(activeStep);
  }

  resetCalculator() {
    this.activeStep = 1;

    this.setProgressLabel(1);
    this.setProgressIndicator(1);
    this.setStepVisibility(1);
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const formData = new FormData(event.target);

    this.getCalculationResult(formData.get("weight"));
    this.setStep(this.activeStep + 1);
  }

  getCalculationResult(weight) {
    this.treesValue.innerText = Math.floor(
      (+weight / 1000) * 0.172 * 100 * 100
    );
    this.kilometersValue.innerText = Math.floor((+weight / 1000) * 4673);
    this.showersValue.innerText = Math.floor((+weight / 1000) * 1000);
    this.papersValue.innerText = Math.floor((+weight / 1000) * 1.088 * 1000);
    this.mealsValue.innerText = Math.floor((+weight / 1000) * 336);
  }

  setStepVisibility(activeStep) {
    this.steps.forEach((step, index) =>
      step.classList[activeStep - 1 === index ? "add" : "remove"](
        selectors.active
      )
    );
  }

  setProgressLabel(activeStep) {
    this.progressLabel.innerText =
      this.progressLabel.dataset.calculatorProgressLabel
        .replace("#", activeStep)
        .replace("#", this.steps.length);
  }

  setProgressIndicator(activeStep) {
    this.progressIndicator.style.setProperty(
      "--quiz-progress",
      (activeStep / this.steps.length) * 100 + "%"
    );
  }
}

document.addEventListener("DOMContentLoaded", async function () {
  const quizContainers = document.querySelectorAll(selectors.quiz);
  const calculatorContainers = document.querySelectorAll(selectors.calculator);

  quizContainers?.forEach((quiz) => new Quiz(quiz));
  calculatorContainers?.forEach((calculator) => new Calculator(calculator));
});
